import {
  ref,
  computed,
  readonly,
  type ComputedRef,
  type DeepReadonly,
  type Ref,
} from "vue";
import type { AdminNotification } from "@/api/model";
import {
  adminV1OrganizationsAdminNotificationsList,
  adminV1OrganizationsAdminNotificationsReadCreate,
  adminV1OrganizationsAdminNotificationsReadAllCreate,
  adminV1OrganizationsAdminNotificationsUnreadCountRetrieve,
  adminV1OrganizationsAdminNotificationsClearAllCreate,
} from "@/api/admin-admin-notifications/admin-admin-notifications";

const POLL_INTERVAL = 30_000; // 30 seconds

// Module-level singleton state — survives Vue Router navigation
const notifications = ref<AdminNotification[]>([]);
const unreadCount = ref(0);
const isLoading = ref(false);

let pollingAbortController: AbortController | null = null;
let isPollingActive = false;
let activeOrganizationId: number | null = null;

interface UseAdminNotificationsReturn {
  notifications: DeepReadonly<Ref<AdminNotification[]>>;
  unreadCount: DeepReadonly<Ref<number>>;
  isLoading: DeepReadonly<Ref<boolean>>;
  hasUnread: ComputedRef<boolean>;
  fetchNotifications: (organizationId: number) => Promise<void>;
  fetchUnreadCount: (organizationId: number) => Promise<void>;
  markAsRead: (organizationId: number, id: string) => Promise<void>;
  markAllAsRead: (organizationId: number) => Promise<void>;
  clearAll: (organizationId: number) => Promise<void>;
  startPolling: (organizationId: number) => void;
  stopPolling: () => void;
}

export function useAdminNotifications(): UseAdminNotificationsReturn {
  const hasUnread = computed(() => unreadCount.value > 0);

  const fetchNotifications = async (organizationId: number): Promise<void> => {
    isLoading.value = true;
    try {
      const response = await adminV1OrganizationsAdminNotificationsList(
        organizationId,
        { limit: 50 }
      );
      notifications.value = response.results ?? [];
    } catch (error) {
      console.error(
        "[AdminNotifications] Failed to fetch notifications",
        error
      );
    } finally {
      isLoading.value = false;
    }
  };

  const fetchUnreadCount = async (organizationId: number): Promise<void> => {
    try {
      const response =
        await adminV1OrganizationsAdminNotificationsUnreadCountRetrieve(
          organizationId
        );
      unreadCount.value = response.count;
    } catch (error) {
      console.error("[AdminNotifications] Failed to fetch unread count", error);
    }
  };

  const markAsRead = async (
    organizationId: number,
    id: string
  ): Promise<void> => {
    try {
      await adminV1OrganizationsAdminNotificationsReadCreate(
        organizationId,
        id
      );
      // Update local state
      const notification = notifications.value.find((n) => n.id === id);
      if (notification) {
        (notification as { is_read: boolean }).is_read = true;
      }
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    } catch (error) {
      console.error("[AdminNotifications] Failed to mark as read", error);
    }
  };

  const markAllAsRead = async (organizationId: number): Promise<void> => {
    try {
      await adminV1OrganizationsAdminNotificationsReadAllCreate(organizationId);
      // Update local state
      notifications.value.forEach((n) => {
        (n as { is_read: boolean }).is_read = true;
      });
      unreadCount.value = 0;
    } catch (error) {
      console.error("[AdminNotifications] Failed to mark all as read", error);
    }
  };

  const clearAll = async (organizationId: number): Promise<void> => {
    try {
      await adminV1OrganizationsAdminNotificationsClearAllCreate(
        organizationId
      );
      notifications.value = [];
      unreadCount.value = 0;
    } catch (error) {
      console.error(
        "[AdminNotifications] Failed to clear notifications",
        error
      );
    }
  };

  const startPolling = (organizationId: number): void => {
    const orgChanged =
      activeOrganizationId !== null && activeOrganizationId !== organizationId;
    activeOrganizationId = organizationId;

    if (orgChanged) {
      notifications.value = [];
      unreadCount.value = 0;
    }

    if (isPollingActive) return;
    isPollingActive = true;
    pollingAbortController = new AbortController();

    const poll = async () => {
      while (isPollingActive && !pollingAbortController?.signal.aborted) {
        if (activeOrganizationId) {
          await fetchUnreadCount(activeOrganizationId);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
    };

    poll();
  };

  const stopPolling = (): void => {
    isPollingActive = false;
    if (pollingAbortController) {
      pollingAbortController.abort();
      pollingAbortController = null;
    }
  };

  return {
    notifications: readonly(notifications),
    unreadCount: readonly(unreadCount),
    isLoading: readonly(isLoading),
    hasUnread,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    startPolling,
    stopPolling,
  };
}
